import { useState, useEffect, useCallback, useRef, type ReactNode } from 'react';
import { getAuthDepartments } from '@/api/auth';
import {
  getMonthSummary,
  getDailySummary,
  getGoodsSummary,
  getReportDetails,
  getCategorySummary,
  getCategoryTotalSummary,
  getMonthPurchasePlanSummary,
  exportReport,
  type ReportFilterParams,
  type ReportSummaryRow,
  type ReportDetailOrder,
  type ReportCategoryMatrix,
  type ReportCategoryTotalMatrix,
  type ReportMonthPurchasePlanRow,
  type ReportGoodsSummaryRow,
  type ReportExportType,
} from '@/api/report';
import { getUser } from '@/utils/auth';
import { alertError } from '@/utils/message';
import SearchableSelect from '@/components/base/SearchableSelect';
import DateRangePicker from '@/components/base/DateRangePicker';
import SpecFilterSelect, { type SpecFilterOption } from '@/components/base/SpecFilterSelect';
import UsageRatioBar from '@/components/base/UsageRatioBar';

type ReportStatMode = NonNullable<ReportFilterParams['stat_mode']>;

const STAT_MODE_OPTIONS: Array<{ id: ReportStatMode; label: string }> = [
  { id: 'complete_time', label: '完成时间' },
  { id: 'created_at', label: '下单时间' },
];

const STAT_MODE_LABELS = STAT_MODE_OPTIONS.map((opt) => opt.label);

type ReportOutsideScope = 'all' | '0' | '1';

const OUTSIDE_SCOPE_OPTIONS: Array<{ id: ReportOutsideScope; label: string }> = [
  { id: 'all', label: '全部' },
  { id: '0', label: '清单内' },
  { id: '1', label: '清单外' },
];

const OUTSIDE_SCOPE_LABELS = OUTSIDE_SCOPE_OPTIONS.map((opt) => opt.label);

/** 当前自然月起止日期 YYYY-MM-DD */
function currentMonthRange(from = new Date()) {
  const y = from.getFullYear();
  const m = from.getMonth();
  const pad = (n: number) => String(n).padStart(2, '0');
  const begin = `${y}-${pad(m + 1)}-01`;
  const end = `${y}-${pad(m + 1)}-${pad(new Date(y, m + 1, 0).getDate())}`;
  return { begin, end };
}

const tabs = [
  { id: 'monthly' as const, label: '月度表', icon: 'ri-calendar-check-line' },
  { id: 'daily' as const, label: '日度表', icon: 'ri-calendar-todo-line' },
  { id: 'goods' as const, label: '物资汇总表', icon: 'ri-box-3-line' },
  { id: 'detail' as const, label: '明细表', icon: 'ri-file-list-3-line' },
  { id: 'category' as const, label: '分类表', icon: 'ri-pie-chart-line' },
  { id: 'categoryTotal' as const, label: '分类合计表', icon: 'ri-stack-line' },
  { id: 'monthPlan' as const, label: '月度计划表', icon: 'ri-calendar-schedule-line' },
];

const TAB_TO_EXPORT_TYPE: Record<(typeof tabs)[number]['id'], ReportExportType> = {
  monthly: 'month-summary',
  daily: 'daily-summary',
  goods: 'goods-summary',
  detail: 'details',
  category: 'category-summary',
  categoryTotal: 'category-total-summary',
  monthPlan: 'month-purchase-summary',
};

interface SummaryViewRow {
  period: string;
  deptName: string;
  goodsQuantity: number;
  amount: number;
}

function mapSummaryRows(rows: ReportSummaryRow[]): SummaryViewRow[] {
  return rows
    .map((row) => ({
      period: String(row.d || '').trim(),
      deptName: String(row.department_name || '未知科室'),
      goodsQuantity: Number(row.goods_quantity ?? 0) || 0,
      amount: Number(row.amount ?? 0) || 0,
    }))
    .filter((row) => !!row.period)
    .sort((a, b) => a.period.localeCompare(b.period) || a.deptName.localeCompare(b.deptName));
}

interface GoodsSummaryViewRow {
  isSubtotal: boolean;
  categoryName: string;
  goodsName: string;
  skuName: string;
  skuCode: string;
  quantity: number;
  unit: string;
  price: number;
  amount: number;
}

function mapGoodsSummaryRows(rows: ReportGoodsSummaryRow[]): GoodsSummaryViewRow[] {
  return rows.map((row) => ({
    isSubtotal: Boolean(row.is_subtotal),
    categoryName: String(row.category_name || '未分类').trim() || '未分类',
    goodsName: String(row.goods_name || '-').trim() || '-',
    skuName: String(row.sku_name || '-').trim() || '-',
    skuCode: String(row.code || '-').trim() || '-',
    quantity: Number(row.quantity ?? 0) || 0,
    unit: String(row.unit || '-').trim() || '-',
    price: Number(row.price ?? 0) || 0,
    amount: Number(row.amount ?? 0) || 0,
  }));
}

interface CategoryMatrixView {
  categories: Array<{ id: string; name: string }>;
  rows: Array<{
    departmentId: string;
    deptName: string;
    amounts: number[];
    total: number;
  }>;
  columnTotals: number[];
  grandTotal: number;
}

function mapCategoryMatrix(data: ReportCategoryMatrix | null | undefined): CategoryMatrixView {
  const empty: CategoryMatrixView = {
    categories: [],
    rows: [],
    columnTotals: [],
    grandTotal: 0,
  };
  if (!data || !Array.isArray(data.categories) || !Array.isArray(data.rows)) {
    return empty;
  }
  const categories = data.categories.map((c) => ({
    id: String(c.id ?? ''),
    name: String(c.name || '未分类').trim() || '未分类',
  }));
  const rows = data.rows.map((row) => {
    const amounts = categories.map((_, idx) => Number(row.amounts?.[idx] ?? 0) || 0);
    return {
      departmentId: String(row.department_id ?? ''),
      deptName: String(row.department_name || '未知科室'),
      amounts,
      total: Number(row.total ?? amounts.reduce((s, n) => s + n, 0)) || 0,
    };
  });
  const columnTotals = categories.map(
    (_, idx) => Number(data.column_totals?.[idx] ?? 0) || 0,
  );
  return {
    categories,
    rows,
    columnTotals,
    grandTotal: Number(data.grand_total ?? 0) || 0,
  };
}

interface CategoryTotalMatrixView {
  categories: Array<{ id: string; name: string }>;
  rows: Array<{
    supplierId: string;
    supplierName: string;
    amounts: number[];
    total: number;
  }>;
  columnTotals: number[];
  grandTotal: number;
}

function mapCategoryTotalMatrix(
  data: ReportCategoryTotalMatrix | null | undefined,
): CategoryTotalMatrixView {
  const empty: CategoryTotalMatrixView = {
    categories: [],
    rows: [],
    columnTotals: [],
    grandTotal: 0,
  };
  if (!data || !Array.isArray(data.categories) || !Array.isArray(data.rows)) {
    return empty;
  }
  const categories = data.categories.map((c) => ({
    id: String(c.id ?? ''),
    name: String(c.name || '未分类').trim() || '未分类',
  }));
  const rows = data.rows.map((row) => {
    const amounts = categories.map((_, idx) => Number(row.amounts?.[idx] ?? 0) || 0);
    return {
      supplierId: String(row.supplier_id ?? ''),
      supplierName: String(row.supplier_name || '未知供应商'),
      amounts,
      total: Number(row.total ?? amounts.reduce((s, n) => s + n, 0)) || 0,
    };
  });
  const columnTotals = categories.map(
    (_, idx) => Number(data.column_totals?.[idx] ?? 0) || 0,
  );
  return {
    categories,
    rows,
    columnTotals,
    grandTotal: Number(data.grand_total ?? 0) || 0,
  };
}

interface MonthPlanViewRow {
  period: string;
  controlTypeText: string;
  deptName: string;
  categoryName: string;
  goodsName: string;
  specName: string;
  code: string;
  unit: string;
  planQuantity: number;
  usedQuantity: number;
  returnQuantity: number;
  remainQuantity: number;
  price: number;
  planAmount: number;
  usedAmount: number;
  returnAmount: number;
  remainAmount: number;
  usageRatio: number;
}

function mapMonthPurchasePlanRows(rows: ReportMonthPurchasePlanRow[]): MonthPlanViewRow[] {
  return rows
    .map((row) => ({
      period: String(row.d || '').trim(),
      controlTypeText: String(row.control_type_text || '-'),
      deptName: String(row.department_name || '全院'),
      categoryName: String(row.category_name || '-'),
      goodsName: String(row.goods_name || '-'),
      specName: String(row.spec_name || '-'),
      code: String(row.code || '-'),
      unit: String(row.unit || '-'),
      planQuantity: Number(row.plan_quantity ?? 0) || 0,
      usedQuantity: Number(row.used_quantity ?? 0) || 0,
      returnQuantity: Number(row.return_quantity ?? 0) || 0,
      remainQuantity: Number(row.remain_quantity ?? 0) || 0,
      price: Number(row.price ?? 0) || 0,
      planAmount: Number(row.plan_amount ?? 0) || 0,
      usedAmount: Number(row.used_amount ?? 0) || 0,
      returnAmount: Number(row.return_amount ?? 0) || 0,
      remainAmount: Number(row.remain_amount ?? 0) || 0,
      usageRatio: Number(row.usage_ratio ?? 0) || 0,
    }))
    .filter((row) => !!row.period);
}

export default function Reports() {
  const [activeTab, setActiveTab] = useState<(typeof tabs)[number]['id']>('monthly');
  const [selectedDepts, setSelectedDepts] = useState<string[]>([]);
  const [statMode, setStatMode] = useState<ReportStatMode>('complete_time');
  const [outsideScope, setOutsideScope] = useState<ReportOutsideScope>('all');
  const [dateFrom, setDateFrom] = useState(() => currentMonthRange().begin);
  const [dateTo, setDateTo] = useState(() => currentMonthRange().end);
  const [selectedSpecs, setSelectedSpecs] = useState<SpecFilterOption[]>([]);

  const [deptOptions, setDeptOptions] = useState<string[]>([]);
  const [deptIdMap, setDeptIdMap] = useState<Record<string, string>>({});
  const deptIdMapRef = useRef(deptIdMap);

  const [monthlySummary, setMonthlySummary] = useState<SummaryViewRow[]>([]);
  const [monthlyLoading, setMonthlyLoading] = useState(false);
  const [monthlyError, setMonthlyError] = useState('');

  const [dailySummary, setDailySummary] = useState<SummaryViewRow[]>([]);
  const [dailyLoading, setDailyLoading] = useState(false);
  const [dailyError, setDailyError] = useState('');

  const [goodsSummary, setGoodsSummary] = useState<GoodsSummaryViewRow[]>([]);
  const [goodsLoading, setGoodsLoading] = useState(false);
  const [goodsError, setGoodsError] = useState('');

  const [detailOrders, setDetailOrders] = useState<ReportDetailOrder[]>([]);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState('');

  const [categoryMatrix, setCategoryMatrix] = useState<CategoryMatrixView>({
    categories: [],
    rows: [],
    columnTotals: [],
    grandTotal: 0,
  });
  const [categoryLoading, setCategoryLoading] = useState(false);
  const [categoryError, setCategoryError] = useState('');

  const [categoryTotalMatrix, setCategoryTotalMatrix] = useState<CategoryTotalMatrixView>({
    categories: [],
    rows: [],
    columnTotals: [],
    grandTotal: 0,
  });
  const [categoryTotalLoading, setCategoryTotalLoading] = useState(false);
  const [categoryTotalError, setCategoryTotalError] = useState('');

  const [monthPlanSummary, setMonthPlanSummary] = useState<MonthPlanViewRow[]>([]);
  const [monthPlanLoading, setMonthPlanLoading] = useState(false);
  const [monthPlanError, setMonthPlanError] = useState('');
  const [exporting, setExporting] = useState(false);

  const user = getUser();
  const showDeptFilter =
    user?.is_divide === true || user?.is_divide === 1 || user?.is_divide === '1';

  useEffect(() => {
    deptIdMapRef.current = deptIdMap;
  }, [deptIdMap]);

  useEffect(() => {
    if (!showDeptFilter) {
      setDeptOptions([]);
      setDeptIdMap({});
      setSelectedDepts([]);
      return;
    }
    let cancelled = false;
    void (async () => {
      try {
        const res = await getAuthDepartments();
        const ok = res.error === false || Number(res.error) === 0;
        if (cancelled || !ok || !Array.isArray(res.data)) return;
        const nextMap: Record<string, string> = {};
        const nextOptions: string[] = [];
        res.data.forEach((item) => {
          const id = Number(item.id);
          const title = String(item.title || '').trim();
          if (!title || !Number.isFinite(id) || id <= 0) return;
          if (!nextMap[title]) {
            nextMap[title] = String(id);
            nextOptions.push(title);
          }
        });
        setDeptIdMap(nextMap);
        setDeptOptions(nextOptions);
      } catch {
        if (!cancelled) {
          setDeptOptions([]);
          setDeptIdMap({});
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [showDeptFilter]);

  /** 所有报表接口共用同一套筛选参数 */
  const buildReportFilterParams = useCallback((): ReportFilterParams => {
    const idMap = deptIdMapRef.current;
    const departmentIds = selectedDepts
      .map((title) => Number(idMap[title]) || idMap[title])
      .filter((id) => id !== '' && id != null && Number(id) > 0);
    const specIds = selectedSpecs
      .map((s) => Number(s.id))
      .filter((id) => Number.isFinite(id) && id > 0);
    return {
      date_begin: dateFrom || undefined,
      date_end: dateTo || undefined,
      department_ids: departmentIds.length > 0 ? departmentIds : undefined,
      spec_ids: specIds.length > 0 ? specIds : undefined,
      stat_mode: statMode,
      ...(outsideScope === 'all' || activeTab === 'monthPlan'
        ? {}
        : { is_outside: Number(outsideScope) as 0 | 1 }),
    };
  }, [selectedDepts, dateFrom, dateTo, selectedSpecs, statMode, outsideScope, activeTab]);

  const fetchMonthSummary = useCallback(async () => {
    setMonthlyLoading(true);
    setMonthlyError('');
    try {
      const res = await getMonthSummary(buildReportFilterParams());
      const ok = res.error === false || Number(res.error) === 0;
      if (!ok) {
        setMonthlySummary([]);
        setMonthlyError(res.message || res.msg || '月度汇总加载失败');
        return;
      }
      setMonthlySummary(mapSummaryRows(Array.isArray(res.data) ? res.data : []));
    } catch {
      setMonthlySummary([]);
      setMonthlyError('月度汇总加载失败，请稍后重试');
    } finally {
      setMonthlyLoading(false);
    }
  }, [buildReportFilterParams]);

  const fetchDailySummary = useCallback(async () => {
    setDailyLoading(true);
    setDailyError('');
    try {
      const res = await getDailySummary(buildReportFilterParams());
      const ok = res.error === false || Number(res.error) === 0;
      if (!ok) {
        setDailySummary([]);
        setDailyError(res.message || res.msg || '日汇总加载失败');
        return;
      }
      setDailySummary(mapSummaryRows(Array.isArray(res.data) ? res.data : []));
    } catch {
      setDailySummary([]);
      setDailyError('日汇总加载失败，请稍后重试');
    } finally {
      setDailyLoading(false);
    }
  }, [buildReportFilterParams]);

  const fetchGoodsSummary = useCallback(async () => {
    setGoodsLoading(true);
    setGoodsError('');
    try {
      const res = await getGoodsSummary(buildReportFilterParams());
      const ok = res.error === false || Number(res.error) === 0;
      if (!ok) {
        setGoodsSummary([]);
        setGoodsError(res.message || res.msg || '物资汇总加载失败');
        return;
      }
      setGoodsSummary(mapGoodsSummaryRows(Array.isArray(res.data) ? res.data : []));
    } catch {
      setGoodsSummary([]);
      setGoodsError('物资汇总加载失败，请稍后重试');
    } finally {
      setGoodsLoading(false);
    }
  }, [buildReportFilterParams]);

  const fetchDetailOrders = useCallback(async () => {
    setDetailLoading(true);
    setDetailError('');
    try {
      const res = await getReportDetails(buildReportFilterParams());
      const ok = res.error === false || Number(res.error) === 0;
      if (!ok) {
        setDetailOrders([]);
        setDetailError(res.message || res.msg || '明细表加载失败');
        return;
      }
      setDetailOrders(Array.isArray(res.data) ? res.data : []);
    } catch {
      setDetailOrders([]);
      setDetailError('明细表加载失败，请稍后重试');
    } finally {
      setDetailLoading(false);
    }
  }, [buildReportFilterParams]);

  const fetchCategorySummary = useCallback(async () => {
    setCategoryLoading(true);
    setCategoryError('');
    try {
      const res = await getCategorySummary(buildReportFilterParams());
      const ok = res.error === false || Number(res.error) === 0;
      if (!ok) {
        setCategoryMatrix({
          categories: [],
          rows: [],
          columnTotals: [],
          grandTotal: 0,
        });
        setCategoryError(res.message || res.msg || '分类统计加载失败');
        return;
      }
      setCategoryMatrix(mapCategoryMatrix(res.data));
    } catch {
      setCategoryMatrix({
        categories: [],
        rows: [],
        columnTotals: [],
        grandTotal: 0,
      });
      setCategoryError('分类统计加载失败，请稍后重试');
    } finally {
      setCategoryLoading(false);
    }
  }, [buildReportFilterParams]);

  const fetchCategoryTotalSummary = useCallback(async () => {
    setCategoryTotalLoading(true);
    setCategoryTotalError('');
    try {
      const res = await getCategoryTotalSummary(buildReportFilterParams());
      const ok = res.error === false || Number(res.error) === 0;
      if (!ok) {
        setCategoryTotalMatrix({
          categories: [],
          rows: [],
          columnTotals: [],
          grandTotal: 0,
        });
        setCategoryTotalError(res.message || res.msg || '分类合计加载失败');
        return;
      }
      setCategoryTotalMatrix(mapCategoryTotalMatrix(res.data));
    } catch {
      setCategoryTotalMatrix({
        categories: [],
        rows: [],
        columnTotals: [],
        grandTotal: 0,
      });
      setCategoryTotalError('分类合计加载失败，请稍后重试');
    } finally {
      setCategoryTotalLoading(false);
    }
  }, [buildReportFilterParams]);

  const fetchMonthPurchasePlanSummary = useCallback(async () => {
    setMonthPlanLoading(true);
    setMonthPlanError('');
    try {
      const params = buildReportFilterParams();
      const res = await getMonthPurchasePlanSummary({
        date_begin: params.date_begin,
        date_end: params.date_end,
        department_ids: params.department_ids,
        spec_ids: params.spec_ids,
      });
      const ok = res.error === false || Number(res.error) === 0;
      if (!ok) {
        setMonthPlanSummary([]);
        setMonthPlanError(res.message || res.msg || '月度计划表加载失败');
        return;
      }
      setMonthPlanSummary(mapMonthPurchasePlanRows(Array.isArray(res.data) ? res.data : []));
    } catch {
      setMonthPlanSummary([]);
      setMonthPlanError('月度计划表加载失败，请稍后重试');
    } finally {
      setMonthPlanLoading(false);
    }
  }, [buildReportFilterParams]);

  useEffect(() => {
    if (activeTab === 'monthly') void fetchMonthSummary();
    if (activeTab === 'daily') void fetchDailySummary();
    if (activeTab === 'goods') void fetchGoodsSummary();
    if (activeTab === 'detail') void fetchDetailOrders();
    if (activeTab === 'category') void fetchCategorySummary();
    if (activeTab === 'categoryTotal') void fetchCategoryTotalSummary();
    if (activeTab === 'monthPlan') void fetchMonthPurchasePlanSummary();
  }, [
    activeTab,
    fetchMonthSummary,
    fetchDailySummary,
    fetchGoodsSummary,
    fetchDetailOrders,
    fetchCategorySummary,
    fetchCategoryTotalSummary,
    fetchMonthPurchasePlanSummary,
  ]);

  const refreshActiveReport = () => {
    if (activeTab === 'monthly') void fetchMonthSummary();
    if (activeTab === 'daily') void fetchDailySummary();
    if (activeTab === 'goods') void fetchGoodsSummary();
    if (activeTab === 'detail') void fetchDetailOrders();
    if (activeTab === 'category') void fetchCategorySummary();
    if (activeTab === 'categoryTotal') void fetchCategoryTotalSummary();
    if (activeTab === 'monthPlan') void fetchMonthPurchasePlanSummary();
  };

  const isApiTabLoading =
    activeTab === 'monthly'
      ? monthlyLoading
      : activeTab === 'daily'
        ? dailyLoading
        : activeTab === 'goods'
          ? goodsLoading
          : activeTab === 'detail'
            ? detailLoading
            : activeTab === 'category'
              ? categoryLoading
              : activeTab === 'categoryTotal'
                ? categoryTotalLoading
                : activeTab === 'monthPlan'
                  ? monthPlanLoading
                  : false;

  const detailOrderCount = detailOrders.length;
  const detailLineCount = detailOrders.reduce(
    (sum, order) => sum + (Array.isArray(order.lists) ? order.lists.length : 0),
    0,
  );

  const defaultMonth = currentMonthRange();
  const hasActiveFilters =
    selectedDepts.length > 0 ||
    selectedSpecs.length > 0 ||
    statMode !== 'complete_time' ||
    (outsideScope !== 'all' && activeTab !== 'monthPlan') ||
    dateFrom !== defaultMonth.begin ||
    dateTo !== defaultMonth.end;

  const statModeLabel =
    STAT_MODE_OPTIONS.find((item) => item.id === statMode)?.label || '完成时间';

  const dateQueryLabel = (() => {
    if (dateFrom && dateTo) return `${statModeLabel}：${dateFrom} 至 ${dateTo}`;
    if (dateFrom) return `${statModeLabel}：${dateFrom} 起`;
    if (dateTo) return `${statModeLabel}：截至 ${dateTo}`;
    return '';
  })();

  const handleReset = () => {
    const month = currentMonthRange();
    setSelectedDepts([]);
    setStatMode('complete_time');
    setOutsideScope('all');
    setDateFrom(month.begin);
    setDateTo(month.end);
    setSelectedSpecs([]);
  };

  const handleExportReport = async () => {
    if (exporting) return;
    setExporting(true);
    try {
      const res = await exportReport({
        ...buildReportFilterParams(),
        report_type: TAB_TO_EXPORT_TYPE[activeTab],
      });
      const ok = res.error === false || Number(res.error) === 0;
      const fileUrl = String(res.url || '').trim();
      if (!ok || !fileUrl) {
        await alertError(res.message || res.msg || '报表导出失败', '导出失败');
        return;
      }
      const fromUrl = decodeURIComponent(
        fileUrl.split('/').pop()?.split('?')[0] || '',
      );
      const filename =
        String(res.filename || '').trim() ||
        fromUrl ||
        '数据报表.xlsx';
      const link = document.createElement('a');
      link.href = fileUrl;
      link.download = filename.endsWith('.xlsx') ? filename : `${filename}.xlsx`;
      link.rel = 'noopener';
      link.style.display = 'none';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch {
      await alertError('报表导出失败，请稍后重试', '导出失败');
    } finally {
      setExporting(false);
    }
  };

  const recordCountLabel =
    activeTab === 'monthly'
      ? monthlySummary.length
      : activeTab === 'daily'
        ? dailySummary.length
        : activeTab === 'goods'
          ? goodsSummary.filter((r) => !r.isSubtotal).length
          : activeTab === 'detail'
            ? `${detailOrderCount} 单 / ${detailLineCount} 行`
            : activeTab === 'monthPlan'
              ? monthPlanSummary.length
              : activeTab === 'categoryTotal'
                ? categoryTotalMatrix.rows.length
                : categoryMatrix.rows.length;

  return (
    <div className="flex-1 bg-background-100">
      <div className="px-4 md:px-6 pb-4 md:pb-6">

        {/* Filter Card */}
        <div className="bg-white rounded-xl px-4 py-3 mb-4">
          <div className="flex items-center gap-3 flex-wrap">
            {/* Filter inputs – same row */}
            <div className="flex items-center gap-3 flex-wrap flex-1">
            {activeTab !== 'monthPlan' && (
            <div className="w-32 shrink-0">
              <SearchableSelect
                options={OUTSIDE_SCOPE_LABELS}
                value={
                  OUTSIDE_SCOPE_OPTIONS.find((item) => item.id === outsideScope)?.label || '全部'
                }
                onChange={(v) => {
                  const label = Array.isArray(v) ? v[0] || '' : v || '';
                  const hit = OUTSIDE_SCOPE_OPTIONS.find((opt) => opt.label === label);
                  setOutsideScope(hit?.id || 'all');
                }}
                placeholder="清单内外"
                icon="ri-file-list-2-line"
                searchPlaceholder="搜索..."
                clearable={false}
              />
            </div>
            )}
            <div className="w-40 shrink-0">
              <SearchableSelect
                options={STAT_MODE_LABELS}
                value={statModeLabel}
                onChange={(v) => {
                  const label = Array.isArray(v) ? v[0] || '' : v || '';
                  const hit = STAT_MODE_OPTIONS.find((opt) => opt.label === label);
                  setStatMode(hit?.id || 'complete_time');
                }}
                placeholder="查询模式"
                icon="ri-time-line"
                searchPlaceholder="搜索模式..."
                clearable={false}
              />
            </div>
            {showDeptFilter && (
              <div className="min-w-[14rem] max-w-xs w-56">
                <SearchableSelect
                  multiple
                  options={deptOptions}
                  value={selectedDepts}
                  onChange={(v) => setSelectedDepts(Array.isArray(v) ? v : v ? [v] : [])}
                  placeholder="搜索 / 选择科室"
                  allLabel="全部科室"
                  icon="ri-hospital-line"
                  searchPlaceholder="继续搜索科室..."
                />
              </div>
            )}
            <div className="flex items-center gap-2">
              <div className="w-56">
                <DateRangePicker
                  dateFrom={dateFrom}
                  dateTo={dateTo}
                  onDateFromChange={setDateFrom}
                  onDateToChange={setDateTo}
                  placeholder={`按${statModeLabel}选择区间`}
                />
              </div>
            </div>
            <div className="flex items-center gap-2">
              <div className="min-w-[14rem] max-w-sm w-72">
                <SpecFilterSelect
                  value={selectedSpecs}
                  onChange={setSelectedSpecs}
                  placeholder="搜索 / 选择规格"
                  searchPlaceholder="继续搜索规格..."
                />
              </div>
            </div>
            </div>{/* end filter inputs */}
            <div className="flex items-center gap-2 shrink-0 self-end">
            {dateQueryLabel && (
              <span className="inline-flex items-center gap-1.5 pl-2.5 pr-1 py-1 rounded-full bg-primary-50 border border-primary-100 text-xs text-primary-700 max-w-full">
                <i className="ri-calendar-check-line shrink-0" />
                <span className="truncate">
                  当前查询时间：
                  <span className="font-medium">{dateQueryLabel}</span>
                </span>
                <button
                  type="button"
                  onClick={() => {
                    const month = currentMonthRange();
                    setDateFrom(month.begin);
                    setDateTo(month.end);
                  }}
                  className="w-5 h-5 flex items-center justify-center rounded-full text-primary-500 hover:bg-primary-100 hover:text-primary-700 transition-colors shrink-0"
                  title="恢复当前月"
                >
                  <i className="ri-close-line text-sm" />
                </button>
              </span>
            )}
            {(activeTab === 'monthly' ||
              activeTab === 'daily' ||
              activeTab === 'goods' ||
              activeTab === 'detail' ||
              activeTab === 'category' ||
              activeTab === 'categoryTotal' ||
              activeTab === 'monthPlan') && (
              <button
                type="button"
                onClick={refreshActiveReport}
                disabled={isApiTabLoading}
                className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs text-foreground-600 hover:text-foreground-800 hover:bg-background-100 transition-colors whitespace-nowrap border border-background-300 disabled:opacity-50"
              >
                <i className={`ri-refresh-line ${isApiTabLoading ? 'animate-spin' : ''}`} />
                刷新
              </button>
            )}
            {hasActiveFilters && (
              <button
                type="button"
                onClick={handleReset}
                className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs text-foreground-500 hover:text-foreground-700 hover:bg-background-100 transition-colors whitespace-nowrap border border-background-300"
              >
                <i className="ri-refresh-line" />
                重置筛选
              </button>
            )}
            </div>
          </div>{/* end flex row */}
        </div>

        {/* Table content Card */}
        <div className="bg-white rounded-xl overflow-hidden">
          <div className="flex items-center justify-between px-4 py-3 border-b border-background-200 bg-background-50 flex-wrap gap-y-2">
            <div className="flex items-center gap-2 flex-wrap">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-all whitespace-nowrap cursor-pointer ${
                    activeTab === tab.id
                      ? 'bg-primary-500 text-background-50'
                      : 'bg-background-200 text-foreground-600 hover:bg-background-300 hover:text-foreground-800'
                  }`}
                >
                  <i className={tab.icon} />
                  {tab.label}
                </button>
              ))}
            </div>
            <div className="flex items-center gap-3 shrink-0">
              <span className="text-xs text-foreground-400">共 {recordCountLabel} 条记录</span>
              <button
                type="button"
                onClick={() => void handleExportReport()}
                disabled={exporting || isApiTabLoading}
                className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-medium text-blue-600 bg-transparent border border-blue-300 hover:bg-blue-50 hover:border-blue-400 transition-colors whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <i className={`ri-download-2-line ${exporting ? 'animate-pulse' : ''}`} />
                {exporting ? '导出中...' : '下载报表'}
              </button>
            </div>
          </div>
          <div className="overflow-x-auto">
            {/* ===== 月度汇总表 ===== */}
            {activeTab === 'monthly' && (
              <table className="w-full text-sm border-collapse [&_th]:border [&_th]:border-background-200 [&_td]:border [&_td]:border-background-100">
                <thead>
                  <tr className="bg-[#f8f8f9]">
                    <th className="py-3 px-3 text-center text-foreground-600 font-semibold text-xs w-14">序号</th>
                    <th className="py-3 px-4 text-center text-foreground-600 font-semibold text-xs">月份</th>
                    <th className="py-3 px-4 text-left text-foreground-600 font-semibold text-xs">科室</th>
                    <th className="py-3 px-4 text-center text-foreground-600 font-semibold text-xs">采购数量</th>
                    <th className="py-3 px-4 text-right text-foreground-600 font-semibold text-xs">采购金额</th>
                  </tr>
                </thead>
                <tbody>
                  {monthlyLoading ? (
                    <tr>
                      <td colSpan={5} className="py-10 text-center text-foreground-400 text-sm">
                        <i className="ri-loader-4-line animate-spin mr-1.5" />
                        正在加载月度汇总...
                      </td>
                    </tr>
                  ) : monthlyError ? (
                    <tr>
                      <td colSpan={5} className="py-10 text-center text-sm">
                        <p className="text-foreground-500 mb-2">{monthlyError}</p>
                        <button
                          type="button"
                          onClick={() => void fetchMonthSummary()}
                          className="text-primary-600 hover:text-primary-700 text-xs"
                        >
                          重新加载
                        </button>
                      </td>
                    </tr>
                  ) : monthlySummary.length === 0 ? (
                    <tr>
                      <td colSpan={5} className="py-8 text-center text-foreground-400 text-sm">暂无数据</td>
                    </tr>
                  ) : (
                    monthlySummary.map((row, idx) => (
                      <tr key={`${row.deptName}-${row.period}-${idx}`} className="hover:bg-background-50 transition-colors">
                        <td className="py-3 px-3 text-center text-foreground-500">{idx + 1}</td>
                        <td className="py-3 px-4 text-center text-foreground-600">{row.period}</td>
                        <td className="py-3 px-4 font-medium text-foreground-800">{row.deptName}</td>
                        <td className="py-3 px-4 text-center text-foreground-600">{row.goodsQuantity}</td>
                        <td className="py-3 px-4 text-right font-semibold text-primary-600">¥{row.amount.toFixed(2)}</td>
                      </tr>
                    ))
                  )}
                </tbody>
                {!monthlyLoading && !monthlyError && monthlySummary.length > 0 && (
                  <tfoot>
                    <tr className="bg-background-50">
                      <td colSpan={3} className="py-3 px-4 text-right text-xs font-medium text-foreground-700">合计</td>
                      <td className="py-3 px-4 text-center text-xs font-bold text-foreground-900">
                        {monthlySummary.reduce((s, r) => s + r.goodsQuantity, 0)}
                      </td>
                      <td className="py-3 px-4 text-right text-sm font-bold text-primary-600">
                        ¥{monthlySummary.reduce((s, r) => s + r.amount, 0).toFixed(2)}
                      </td>
                    </tr>
                  </tfoot>
                )}
              </table>
            )}

            {/* ===== 日汇总表 ===== */}
            {activeTab === 'daily' && (
              <table className="w-full text-sm border-collapse [&_th]:border [&_th]:border-background-200 [&_td]:border [&_td]:border-background-100">
                <thead>
                  <tr className="bg-[#f8f8f9]">
                    <th className="py-3 px-3 text-center text-foreground-600 font-semibold text-xs w-14">序号</th>
                    <th className="py-3 px-4 text-center text-foreground-600 font-semibold text-xs">日期</th>
                    <th className="py-3 px-4 text-left text-foreground-600 font-semibold text-xs">科室</th>
                    <th className="py-3 px-4 text-center text-foreground-600 font-semibold text-xs">采购数量</th>
                    <th className="py-3 px-4 text-right text-foreground-600 font-semibold text-xs">采购金额</th>
                  </tr>
                </thead>
                <tbody>
                  {dailyLoading ? (
                    <tr>
                      <td colSpan={5} className="py-10 text-center text-foreground-400 text-sm">
                        <i className="ri-loader-4-line animate-spin mr-1.5" />
                        正在加载日汇总...
                      </td>
                    </tr>
                  ) : dailyError ? (
                    <tr>
                      <td colSpan={5} className="py-10 text-center text-sm">
                        <p className="text-foreground-500 mb-2">{dailyError}</p>
                        <button
                          type="button"
                          onClick={() => void fetchDailySummary()}
                          className="text-primary-600 hover:text-primary-700 text-xs"
                        >
                          重新加载
                        </button>
                      </td>
                    </tr>
                  ) : dailySummary.length === 0 ? (
                    <tr>
                      <td colSpan={5} className="py-8 text-center text-foreground-400 text-sm">暂无数据</td>
                    </tr>
                  ) : (
                    dailySummary.map((row, idx) => (
                      <tr key={`${row.deptName}-${row.period}-${idx}`} className="hover:bg-background-50 transition-colors">
                        <td className="py-3 px-3 text-center text-foreground-500">{idx + 1}</td>
                        <td className="py-3 px-4 text-center text-foreground-600">{row.period}</td>
                        <td className="py-3 px-4 font-medium text-foreground-800">{row.deptName}</td>
                        <td className="py-3 px-4 text-center text-foreground-600">{row.goodsQuantity}</td>
                        <td className="py-3 px-4 text-right font-semibold text-primary-600">¥{row.amount.toFixed(2)}</td>
                      </tr>
                    ))
                  )}
                </tbody>
                {!dailyLoading && !dailyError && dailySummary.length > 0 && (
                  <tfoot>
                    <tr className="bg-background-50">
                      <td colSpan={3} className="py-3 px-4 text-right text-xs font-medium text-foreground-700">合计</td>
                      <td className="py-3 px-4 text-center text-xs font-bold text-foreground-900">
                        {dailySummary.reduce((s, r) => s + r.goodsQuantity, 0)}
                      </td>
                      <td className="py-3 px-4 text-right text-sm font-bold text-primary-600">
                        ¥{dailySummary.reduce((s, r) => s + r.amount, 0).toFixed(2)}
                      </td>
                    </tr>
                  </tfoot>
                )}
              </table>
            )}

            {/* ===== 物资汇总表 ===== */}
            {activeTab === 'goods' && (
              <table className="w-full text-sm border-collapse [&_th]:border [&_th]:border-background-200 [&_td]:border [&_td]:border-background-100">
                <thead>
                  <tr className="bg-[#f8f8f9]">
                    <th className="py-3 px-3 text-center text-foreground-600 font-semibold text-xs whitespace-nowrap w-14">序号</th>
                    <th className="py-3 px-3 text-left text-foreground-600 font-semibold text-xs whitespace-nowrap">物资类别</th>
                    <th className="py-3 px-3 text-left text-foreground-600 font-semibold text-xs whitespace-nowrap">物资名称</th>
                    <th className="py-3 px-3 text-left text-foreground-600 font-semibold text-xs whitespace-nowrap">规格型号</th>
                    <th className="py-3 px-3 text-left text-foreground-600 font-semibold text-xs whitespace-nowrap">商品编码</th>
                    <th className="py-3 px-3 text-center text-foreground-600 font-semibold text-xs whitespace-nowrap">单位</th>
                    <th className="py-3 px-3 text-right text-foreground-600 font-semibold text-xs whitespace-nowrap">单价</th>
                    <th className="py-3 px-3 text-center text-foreground-600 font-semibold text-xs whitespace-nowrap">采购数量</th>
                    <th className="py-3 px-3 text-right text-foreground-600 font-semibold text-xs whitespace-nowrap">合计金额</th>
                  </tr>
                </thead>
                <tbody>
                  {goodsLoading ? (
                    <tr>
                      <td colSpan={9} className="py-10 text-center text-foreground-400 text-sm">
                        <i className="ri-loader-4-line animate-spin mr-1.5" />
                        正在加载物资汇总...
                      </td>
                    </tr>
                  ) : goodsError ? (
                    <tr>
                      <td colSpan={9} className="py-10 text-center text-sm">
                        <p className="text-foreground-500 mb-2">{goodsError}</p>
                        <button
                          type="button"
                          onClick={() => void fetchGoodsSummary()}
                          className="text-primary-600 hover:text-primary-700 text-xs"
                        >
                          重新加载
                        </button>
                      </td>
                    </tr>
                  ) : goodsSummary.length === 0 ? (
                    <tr>
                      <td colSpan={9} className="py-8 text-center text-foreground-400 text-sm">暂无数据</td>
                    </tr>
                  ) : (
                    (() => {
                      let lineNo = 0;
                      return goodsSummary.map((row, idx) => {
                        if (row.isSubtotal) {
                          return (
                            <tr key={`cat-total-${row.categoryName}-${idx}`} className="bg-[#f3f6f9]">
                              <td
                                colSpan={7}
                                className="py-2.5 px-3 text-right text-xs font-semibold text-foreground-700"
                              >
                                {row.categoryName}合计
                              </td>
                              <td
                                className={`py-2.5 px-3 text-center whitespace-nowrap text-xs font-bold ${
                                  row.quantity < 0 ? 'text-orange-600' : 'text-foreground-900'
                                }`}
                              >
                                {row.quantity}
                              </td>
                              <td
                                className={`py-2.5 px-3 text-right font-bold whitespace-nowrap text-xs ${
                                  row.amount < 0 ? 'text-orange-600' : 'text-primary-600'
                                }`}
                              >
                                ¥{row.amount.toFixed(2)}
                              </td>
                            </tr>
                          );
                        }
                        lineNo += 1;
                        return (
                          <tr
                            key={`${row.goodsName}-${row.skuName}-${idx}`}
                            className="hover:bg-background-50 transition-colors"
                          >
                            <td className="py-2.5 px-3 text-center text-foreground-500 text-xs">{lineNo}</td>
                            <td className="py-2.5 px-3 text-foreground-700 text-xs max-w-[160px] truncate">
                              {row.categoryName}
                            </td>
                            <td className="py-2.5 px-3 text-foreground-800 text-xs max-w-[160px] truncate">
                              {row.goodsName}
                            </td>
                            <td className="py-2.5 px-3 text-foreground-600 text-xs max-w-[140px] truncate">
                              {row.skuName}
                            </td>
                            <td className="py-2.5 px-3 text-foreground-600 text-xs max-w-[140px] truncate">
                              {row.skuCode}
                            </td>
                            <td className="py-2.5 px-3 text-center text-foreground-600 whitespace-nowrap text-xs">
                              {row.unit}
                            </td>
                            <td className="py-2.5 px-3 text-right text-foreground-700 whitespace-nowrap text-xs">
                              ¥{row.price.toFixed(2)}
                            </td>
                            <td
                              className={`py-2.5 px-3 text-center whitespace-nowrap text-xs ${
                                row.quantity < 0 ? 'text-orange-600 font-medium' : 'text-foreground-700'
                              }`}
                            >
                              {row.quantity}
                            </td>
                            <td
                              className={`py-2.5 px-3 text-right font-semibold whitespace-nowrap text-xs ${
                                row.amount < 0 ? 'text-orange-600' : 'text-primary-600'
                              }`}
                            >
                              ¥{row.amount.toFixed(2)}
                            </td>
                          </tr>
                        );
                      });
                    })()
                  )}
                </tbody>
                {!goodsLoading && !goodsError && goodsSummary.length > 0 && (
                  <tfoot>
                    <tr className="bg-background-50">
                      <td colSpan={7} className="py-3 px-3 text-right text-xs font-medium text-foreground-700">
                        合计
                      </td>
                      <td className="py-3 px-3 text-center text-xs font-bold text-foreground-900">
                        {goodsSummary.filter((r) => !r.isSubtotal).reduce((s, r) => s + r.quantity, 0)}
                      </td>
                      <td className="py-3 px-3 text-right text-sm font-bold text-primary-600">
                        ¥{goodsSummary.filter((r) => !r.isSubtotal).reduce((s, r) => s + r.amount, 0).toFixed(2)}
                      </td>
                    </tr>
                  </tfoot>
                )}
              </table>
            )}

            {/* ===== 明细表 ===== */}
            {activeTab === 'detail' && (
              <table className="w-full text-sm border-collapse [&_th]:border [&_th]:border-background-200 [&_td]:border [&_td]:border-background-100">
                <thead>
                  <tr className="bg-[#f8f8f9]">
                    <th className="py-3 px-3 text-center text-foreground-600 font-semibold text-xs whitespace-nowrap w-14">序号</th>
                    <th className="py-3 px-3 text-left text-foreground-600 font-semibold text-xs whitespace-nowrap">科室名称</th>
                    <th className="py-3 px-3 text-center text-foreground-600 font-semibold text-xs whitespace-nowrap">
                      {statMode === 'created_at' ? '下单日期' : '完成日期'}
                    </th>
                    <th className="py-3 px-3 text-left text-foreground-600 font-semibold text-xs whitespace-nowrap">所属分类</th>
                    <th className="py-3 px-3 text-left text-foreground-600 font-semibold text-xs whitespace-nowrap">订单号</th>
                    <th className="py-3 px-3 text-left text-foreground-600 font-semibold text-xs whitespace-nowrap">物资名</th>
                    <th className="py-3 px-3 text-left text-foreground-600 font-semibold text-xs whitespace-nowrap">规格型号</th>
                    <th className="py-3 px-3 text-left text-foreground-600 font-semibold text-xs whitespace-nowrap">商品编码</th>
                    <th className="py-3 px-3 text-center text-foreground-600 font-semibold text-xs whitespace-nowrap">单位</th>
                    <th className="py-3 px-3 text-right text-foreground-600 font-semibold text-xs whitespace-nowrap">单价</th>
                    <th className="py-3 px-3 text-center text-foreground-600 font-semibold text-xs whitespace-nowrap">数量</th>
                    <th className="py-3 px-3 text-right text-foreground-600 font-semibold text-xs whitespace-nowrap">商品金额</th>
                    <th className="py-3 px-3 text-left text-foreground-600 font-semibold text-xs whitespace-nowrap">备注</th>
                  </tr>
                </thead>
                <tbody>
                  {detailLoading ? (
                    <tr>
                      <td colSpan={13} className="py-10 text-center text-foreground-400 text-sm">
                        <i className="ri-loader-4-line animate-spin mr-1.5" />
                        正在加载明细...
                      </td>
                    </tr>
                  ) : detailError ? (
                    <tr>
                      <td colSpan={13} className="py-10 text-center text-sm">
                        <p className="text-foreground-500 mb-2">{detailError}</p>
                        <button
                          type="button"
                          onClick={() => void fetchDetailOrders()}
                          className="text-primary-600 hover:text-primary-700 text-xs"
                        >
                          重新加载
                        </button>
                      </td>
                    </tr>
                  ) : detailOrders.length === 0 ? (
                    <tr>
                      <td colSpan={13} className="py-8 text-center text-foreground-400 text-sm">暂无数据</td>
                    </tr>
                  ) : (
                    (() => {
                      const rows: ReactNode[] = [];
                      let lineNo = 0;
                      let deptKey: string | null = null;
                      let deptLabel = '';
                      let deptQty = 0;
                      let deptGoodsAmount = 0;

                      const flushDeptTotal = () => {
                        if (deptKey === null) return;
                        const label =
                          deptLabel && deptLabel !== '-' ? `${deptLabel}合计` : '科室合计';
                        rows.push(
                          <tr key={`dept-total-${deptKey}`} className="bg-[#f3f6f9]">
                            <td
                              colSpan={10}
                              className="py-2.5 px-3 text-right text-xs font-semibold text-foreground-700"
                            >
                              {label}
                            </td>
                            <td
                              className={`py-2.5 px-3 text-center text-xs font-bold whitespace-nowrap ${
                                deptQty < 0 ? 'text-orange-600' : 'text-foreground-900'
                              }`}
                            >
                              {deptQty}
                            </td>
                            <td
                              className={`py-2.5 px-3 text-right text-xs font-bold whitespace-nowrap ${
                                deptGoodsAmount < 0 ? 'text-orange-600' : 'text-primary-600'
                              }`}
                            >
                              ¥{deptGoodsAmount.toFixed(2)}
                            </td>
                            <td className="py-2.5 px-3" />
                          </tr>,
                        );
                        deptQty = 0;
                        deptGoodsAmount = 0;
                      };

                      detailOrders.forEach((order) => {
                        const currentDeptKey = String(
                          order.department_id ?? order.department_name ?? '',
                        );
                        const deptName = order.department_name || '-';
                        if (deptKey !== null && currentDeptKey !== deptKey) {
                          flushDeptTotal();
                        }
                        deptKey = currentDeptKey;
                        deptLabel = deptName;
                        const items =
                          Array.isArray(order.lists) && order.lists.length > 0
                            ? order.lists
                            : [null];
                        const qtyFromApi = Number(order.goods_quantity);
                        const amountFromApi = Number(order.goods_amount);
                        const orderQty = Number.isFinite(qtyFromApi)
                          ? qtyFromApi
                          : items.reduce(
                              (s, item) => s + (item ? Number(item.quantity ?? 0) || 0 : 0),
                              0,
                            );
                        const orderGoodsAmount = Number.isFinite(amountFromApi)
                          ? amountFromApi
                          : items.reduce(
                              (s, item) => s + (item ? Number(item.amount ?? 0) || 0 : 0),
                              0,
                            );
                        deptQty += orderQty;
                        deptGoodsAmount += orderGoodsAmount;
                        const orderDate =
                          statMode === 'created_at'
                            ? order.created_at
                              ? String(order.created_at).slice(0, 10)
                              : '-'
                            : order.complete_time
                              ? String(order.complete_time).slice(0, 10)
                              : order.created_at
                                ? String(order.created_at).slice(0, 10)
                                : '-';

                        items.forEach((item, itemIdx) => {
                          lineNo += 1;
                          const qty = item ? Number(item.quantity ?? 0) || 0 : 0;
                          const price = item ? Number(item.price ?? 0) || 0 : 0;
                          const goodsAmount = item ? Number(item.amount ?? 0) || 0 : 0;
                          rows.push(
                            <tr
                              key={`${order.id}-${item?.id ?? 'empty'}-${itemIdx}`}
                              className="hover:bg-background-50 transition-colors"
                            >
                              <td className="py-2.5 px-3 text-center text-foreground-500 text-xs">
                                {lineNo}
                              </td>
                              <td className="py-2.5 px-3 text-foreground-700 whitespace-nowrap text-xs">
                                {deptName}
                              </td>
                              <td className="py-2.5 px-3 text-center text-foreground-700 whitespace-nowrap text-xs">
                                {orderDate}
                              </td>
                              <td className="py-2.5 px-3 text-foreground-600 whitespace-nowrap text-xs">
                                {item?.category_name || '未分类'}
                              </td>
                              <td className="py-2.5 px-3 text-foreground-800 font-medium whitespace-nowrap text-xs">
                                {order.order_sn || order.id}
                              </td>
                              <td className="py-2.5 px-3 text-foreground-800 max-w-[160px] truncate text-xs">
                                {item?.goods_name || '-'}
                              </td>
                              <td className="py-2.5 px-3 text-foreground-600 max-w-[120px] truncate text-xs">
                                {item?.sku_name || '-'}
                              </td>
                              <td className="py-2.5 px-3 text-foreground-600 max-w-[140px] truncate text-xs">
                                {item?.sku || item?.code || '-'}
                              </td>
                              <td className="py-2.5 px-3 text-center text-foreground-600 whitespace-nowrap text-xs">
                                {item?.unit || '-'}
                              </td>
                              <td className="py-2.5 px-3 text-right text-foreground-700 whitespace-nowrap text-xs">
                                {item ? `¥${price.toFixed(2)}` : '-'}
                              </td>
                              <td
                                className={`py-2.5 px-3 text-center whitespace-nowrap text-xs ${
                                  item && qty < 0
                                    ? 'text-orange-600 font-medium'
                                    : 'text-foreground-700'
                                }`}
                              >
                                {item ? qty : '-'}
                              </td>
                              <td
                                className={`py-2.5 px-3 text-right font-semibold whitespace-nowrap text-xs ${
                                  item && goodsAmount < 0 ? 'text-orange-600' : 'text-primary-600'
                                }`}
                              >
                                {item ? `¥${goodsAmount.toFixed(2)}` : '-'}
                              </td>
                              <td className="py-2.5 px-3 text-foreground-600 max-w-[180px] truncate text-xs">
                                {item?.remark || '-'}
                              </td>
                            </tr>,
                          );
                        });
                      });

                      flushDeptTotal();
                      return rows;
                    })()
                  )}
                </tbody>
                {!detailLoading && !detailError && detailOrders.length > 0 && (
                  <tfoot>
                    {(() => {
                      const totalQty = detailOrders.reduce((s, o) => {
                        const fromApi = Number(o.goods_quantity);
                        if (Number.isFinite(fromApi)) return s + fromApi;
                        return (
                          s +
                          (Array.isArray(o.lists) ? o.lists : []).reduce(
                            (qs, item) => qs + (Number(item?.quantity ?? 0) || 0),
                            0,
                          )
                        );
                      }, 0);
                      const totalGoodsAmount = detailOrders.reduce((s, o) => {
                        const fromApi = Number(o.goods_amount);
                        if (Number.isFinite(fromApi)) return s + fromApi;
                        return (
                          s +
                          (Array.isArray(o.lists) ? o.lists : []).reduce(
                            (as, item) => as + (Number(item?.amount ?? 0) || 0),
                            0,
                          )
                        );
                      }, 0);
                      return (
                        <tr className="bg-background-50">
                          <td
                            colSpan={10}
                            className="py-3 px-3 text-right text-xs font-semibold text-foreground-700"
                          >
                            总合计
                          </td>
                          <td
                            className={`py-3 px-3 text-center text-xs font-bold whitespace-nowrap ${
                              totalQty < 0 ? 'text-orange-600' : 'text-foreground-900'
                            }`}
                          >
                            {totalQty}
                          </td>
                          <td
                            className={`py-3 px-3 text-right text-sm font-bold whitespace-nowrap ${
                              totalGoodsAmount < 0 ? 'text-orange-600' : 'text-primary-600'
                            }`}
                          >
                            ¥{totalGoodsAmount.toFixed(2)}
                          </td>
                          <td className="py-3 px-3" />
                        </tr>
                      );
                    })()}
                  </tfoot>
                )}
              </table>
            )}

            {/* ===== 月度计划表 ===== */}
            {activeTab === 'monthPlan' && (
              <table className="w-full text-sm border-collapse [&_th]:border [&_th]:border-background-200 [&_td]:border [&_td]:border-background-100">
                <thead>
                  <tr className="bg-[#f8f8f9]">
                    <th className="py-3 px-2 text-center text-foreground-600 font-semibold text-xs w-12 whitespace-nowrap">序号</th>
                    <th className="py-3 px-3 text-center text-foreground-600 font-semibold text-xs whitespace-nowrap">月份</th>
                    <th className="py-3 px-3 text-left text-foreground-600 font-semibold text-xs whitespace-nowrap">控制类型</th>
                    <th className="py-3 px-3 text-left text-foreground-600 font-semibold text-xs whitespace-nowrap">科室名称</th>
                    <th className="py-3 px-3 text-left text-foreground-600 font-semibold text-xs whitespace-nowrap">分类</th>
                    <th className="py-3 px-3 text-left text-foreground-600 font-semibold text-xs whitespace-nowrap">物资名称</th>
                    <th className="py-3 px-3 text-left text-foreground-600 font-semibold text-xs whitespace-nowrap">规格名称</th>
                    <th className="py-3 px-3 text-left text-foreground-600 font-semibold text-xs whitespace-nowrap">编码</th>
                    <th className="py-3 px-3 text-center text-foreground-600 font-semibold text-xs whitespace-nowrap">单位</th>
                    <th className="py-3 px-3 text-center text-foreground-600 font-semibold text-xs whitespace-nowrap">计划数</th>
                    <th className="py-3 px-3 text-center text-foreground-600 font-semibold text-xs whitespace-nowrap">已使用</th>
                    <th className="py-3 px-3 text-center text-foreground-600 font-semibold text-xs whitespace-nowrap">已退货</th>
                    <th className="py-3 px-3 text-center text-foreground-600 font-semibold text-xs whitespace-nowrap">剩余可用</th>
                    <th className="py-3 px-3 text-right text-foreground-600 font-semibold text-xs whitespace-nowrap">单价</th>
                    <th className="py-3 px-3 text-right text-foreground-600 font-semibold text-xs whitespace-nowrap">采购总价</th>
                    <th className="py-3 px-3 text-right text-foreground-600 font-semibold text-xs whitespace-nowrap">已使用总价</th>
                    <th className="py-3 px-3 text-right text-foreground-600 font-semibold text-xs whitespace-nowrap">退货金额</th>
                    <th className="py-3 px-3 text-right text-foreground-600 font-semibold text-xs whitespace-nowrap">剩余金额</th>
                    <th className="py-3 px-3 text-left text-foreground-600 font-semibold text-xs whitespace-nowrap">已使用比例</th>
                  </tr>
                </thead>
                <tbody>
                  {monthPlanLoading ? (
                    <tr>
                      <td colSpan={19} className="py-10 text-center text-foreground-400 text-sm">
                        <i className="ri-loader-4-line animate-spin mr-1.5" />
                        正在加载月度计划表...
                      </td>
                    </tr>
                  ) : monthPlanError ? (
                    <tr>
                      <td colSpan={19} className="py-10 text-center text-sm">
                        <p className="text-foreground-500 mb-2">{monthPlanError}</p>
                        <button
                          type="button"
                          onClick={() => void fetchMonthPurchasePlanSummary()}
                          className="text-primary-600 hover:text-primary-700 text-xs"
                        >
                          重新加载
                        </button>
                      </td>
                    </tr>
                  ) : monthPlanSummary.length === 0 ? (
                    <tr>
                      <td colSpan={19} className="py-8 text-center text-foreground-400 text-sm">
                        暂无月度采购计划数据
                      </td>
                    </tr>
                  ) : (
                    monthPlanSummary.map((row, idx) => (
                      <tr
                        key={`${row.period}-${row.controlTypeText}-${row.deptName}-${row.code}-${idx}`}
                        className="hover:bg-background-50 transition-colors"
                      >
                        <td className="py-2.5 px-2 text-center text-foreground-500 text-xs">{idx + 1}</td>
                        <td className="py-2.5 px-3 text-center text-foreground-600 text-xs whitespace-nowrap">{row.period}</td>
                        <td className="py-2.5 px-3 text-foreground-700 text-xs whitespace-nowrap">{row.controlTypeText}</td>
                        <td className="py-2.5 px-3 text-foreground-800 text-xs whitespace-nowrap">{row.deptName}</td>
                        <td className="py-2.5 px-3 text-foreground-600 text-xs whitespace-nowrap">{row.categoryName}</td>
                        <td className="py-2.5 px-3 text-foreground-800 text-xs max-w-[140px] truncate">{row.goodsName}</td>
                        <td className="py-2.5 px-3 text-foreground-600 text-xs max-w-[120px] truncate">{row.specName}</td>
                        <td className="py-2.5 px-3 text-foreground-500 text-xs whitespace-nowrap">{row.code}</td>
                        <td className="py-2.5 px-3 text-center text-foreground-600 text-xs whitespace-nowrap">{row.unit}</td>
                        <td className="py-2.5 px-3 text-center text-foreground-700 text-xs font-medium whitespace-nowrap">{row.planQuantity}</td>
                        <td className="py-2.5 px-3 text-center text-foreground-700 text-xs whitespace-nowrap">{row.usedQuantity}</td>
                        <td className="py-2.5 px-3 text-center text-foreground-600 text-xs whitespace-nowrap">{row.returnQuantity}</td>
                        <td className="py-2.5 px-3 text-center text-foreground-800 text-xs font-medium whitespace-nowrap">{row.remainQuantity}</td>
                        <td className="py-2.5 px-3 text-right text-foreground-700 text-xs whitespace-nowrap">¥{row.price.toFixed(2)}</td>
                        <td className="py-2.5 px-3 text-right text-foreground-700 text-xs whitespace-nowrap">¥{row.planAmount.toFixed(2)}</td>
                        <td className="py-2.5 px-3 text-right text-foreground-700 text-xs whitespace-nowrap">¥{row.usedAmount.toFixed(2)}</td>
                        <td className="py-2.5 px-3 text-right text-foreground-600 text-xs whitespace-nowrap">¥{row.returnAmount.toFixed(2)}</td>
                        <td className="py-2.5 px-3 text-right text-primary-600 text-xs font-semibold whitespace-nowrap">¥{row.remainAmount.toFixed(2)}</td>
                        <td className="py-2.5 px-3 text-left">
                          <UsageRatioBar ratio={row.usageRatio} />
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
                {!monthPlanLoading && !monthPlanError && monthPlanSummary.length > 0 && (
                  <tfoot>
                    {(() => {
                      const totalPlanAmount = monthPlanSummary.reduce((s, r) => s + r.planAmount, 0);
                      const totalUsedAmount = monthPlanSummary.reduce((s, r) => s + r.usedAmount, 0);
                      const totalReturnAmount = monthPlanSummary.reduce((s, r) => s + r.returnAmount, 0);
                      const totalRemainAmount = monthPlanSummary.reduce((s, r) => s + r.remainAmount, 0);
                      const usedRatio =
                        totalPlanAmount > 0
                          ? ((totalUsedAmount - totalReturnAmount) / totalPlanAmount) * 100
                          : 0;
                      return (
                        <tr className="bg-background-50">
                          <td colSpan={9} className="py-3 px-3 text-right text-xs font-medium text-foreground-700">
                            合计
                          </td>
                          <td className="py-3 px-3 text-center text-xs font-bold text-foreground-900">
                            {monthPlanSummary.reduce((s, r) => s + r.planQuantity, 0)}
                          </td>
                          <td className="py-3 px-3 text-center text-xs font-bold text-foreground-900">
                            {monthPlanSummary.reduce((s, r) => s + r.usedQuantity, 0)}
                          </td>
                          <td className="py-3 px-3 text-center text-xs font-bold text-foreground-900">
                            {monthPlanSummary.reduce((s, r) => s + r.returnQuantity, 0)}
                          </td>
                          <td className="py-3 px-3 text-center text-xs font-bold text-foreground-900">
                            {monthPlanSummary.reduce((s, r) => s + r.remainQuantity, 0)}
                          </td>
                          <td />
                          <td className="py-3 px-3 text-right text-xs font-bold text-foreground-900">
                            ¥{totalPlanAmount.toFixed(2)}
                          </td>
                          <td className="py-3 px-3 text-right text-xs font-bold text-foreground-900">
                            ¥{totalUsedAmount.toFixed(2)}
                          </td>
                          <td className="py-3 px-3 text-right text-xs font-bold text-foreground-900">
                            ¥{totalReturnAmount.toFixed(2)}
                          </td>
                          <td className="py-3 px-3 text-right text-sm font-bold text-primary-600">
                            ¥{totalRemainAmount.toFixed(2)}
                          </td>
                          <td className="py-3 px-3 text-left">
                            <UsageRatioBar ratio={usedRatio} />
                          </td>
                        </tr>
                      );
                    })()}
                  </tfoot>
                )}
              </table>
            )}

            {/* ===== 分类统计表：科室纵向 × 一级分类横向 ===== */}
            {activeTab === 'category' && (
              <div className="overflow-x-auto">
                <table className="w-full text-sm border-collapse min-w-max [&_th]:border [&_th]:border-background-200 [&_td]:border [&_td]:border-background-100">
                  <thead>
                    <tr className="bg-[#f8f8f9]">
                      <th className="py-3 px-4 text-left text-foreground-600 font-semibold text-xs whitespace-nowrap sticky left-0 bg-[#f8f8f9] z-10">
                        科室名称
                      </th>
                      {categoryMatrix.categories.map((cat) => (
                        <th
                          key={cat.id}
                          className="py-3 px-4 text-right text-foreground-600 font-semibold text-xs whitespace-nowrap"
                        >
                          {cat.name}
                        </th>
                      ))}
                      <th className="py-3 px-4 text-right text-foreground-600 font-semibold text-xs whitespace-nowrap">
                        采购金额
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {categoryLoading ? (
                      <tr>
                        <td
                          colSpan={Math.max(2, categoryMatrix.categories.length + 2)}
                          className="py-10 text-center text-foreground-400 text-sm"
                        >
                          <i className="ri-loader-4-line animate-spin mr-1.5" />
                          正在加载分类统计...
                        </td>
                      </tr>
                    ) : categoryError ? (
                      <tr>
                        <td
                          colSpan={Math.max(2, categoryMatrix.categories.length + 2)}
                          className="py-10 text-center text-sm"
                        >
                          <p className="text-foreground-500 mb-2">{categoryError}</p>
                          <button
                            type="button"
                            onClick={() => void fetchCategorySummary()}
                            className="text-primary-600 hover:text-primary-700 text-xs"
                          >
                            重新加载
                          </button>
                        </td>
                      </tr>
                    ) : categoryMatrix.rows.length === 0 ? (
                      <tr>
                        <td
                          colSpan={Math.max(2, categoryMatrix.categories.length + 2)}
                          className="py-8 text-center text-foreground-400 text-sm"
                        >
                          暂无数据
                        </td>
                      </tr>
                    ) : (
                      categoryMatrix.rows.map((row) => (
                        <tr
                          key={row.departmentId}
                          className="hover:bg-background-50 transition-colors"
                        >
                          <td className="py-3 px-4 font-medium text-foreground-800 whitespace-nowrap sticky left-0 bg-white">
                            {row.deptName}
                          </td>
                          {row.amounts.map((amount, idx) => (
                            <td
                              key={`${row.departmentId}-${categoryMatrix.categories[idx]?.id ?? idx}`}
                              className={`py-3 px-4 text-right whitespace-nowrap ${
                                amount < 0 ? 'text-orange-600' : 'text-foreground-700'
                              }`}
                            >
                              ¥{amount.toFixed(2)}
                            </td>
                          ))}
                          <td
                            className={`py-3 px-4 text-right font-semibold whitespace-nowrap ${
                              row.total < 0 ? 'text-orange-600' : 'text-primary-600'
                            }`}
                          >
                            ¥{row.total.toFixed(2)}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                  {!categoryLoading && !categoryError && categoryMatrix.rows.length > 0 && (
                    <tfoot>
                      <tr className="bg-background-50">
                        <td className="py-3 px-4 text-xs font-semibold text-foreground-700 sticky left-0 bg-background-50">
                          合计
                        </td>
                        {categoryMatrix.columnTotals.map((total, idx) => (
                          <td
                            key={`col-total-${categoryMatrix.categories[idx]?.id ?? idx}`}
                            className={`py-3 px-4 text-right text-xs font-bold whitespace-nowrap ${
                              total < 0 ? 'text-orange-600' : 'text-foreground-900'
                            }`}
                          >
                            ¥{total.toFixed(2)}
                          </td>
                        ))}
                        <td
                          className={`py-3 px-4 text-right text-sm font-bold whitespace-nowrap ${
                            categoryMatrix.grandTotal < 0 ? 'text-orange-600' : 'text-primary-600'
                          }`}
                        >
                          ¥{categoryMatrix.grandTotal.toFixed(2)}
                        </td>
                      </tr>
                    </tfoot>
                  )}
                </table>
              </div>
            )}

            {/* ===== 分类合计表：供应商纵向 × 一级分类横向 ===== */}
            {activeTab === 'categoryTotal' && (
              <div className="overflow-x-auto">
                <table className="w-full text-sm border-collapse min-w-max [&_th]:border [&_th]:border-background-200 [&_td]:border [&_td]:border-background-100">
                  <thead>
                    <tr className="bg-[#f8f8f9]">
                      <th className="py-3 px-4 text-left text-foreground-600 font-semibold text-xs whitespace-nowrap sticky left-0 bg-[#f8f8f9] z-10">
                        供应商
                      </th>
                      {categoryTotalMatrix.categories.map((cat) => (
                        <th
                          key={cat.id}
                          className="py-3 px-4 text-right text-foreground-600 font-semibold text-xs whitespace-nowrap"
                        >
                          {cat.name}
                        </th>
                      ))}
                      <th className="py-3 px-4 text-right text-foreground-600 font-semibold text-xs whitespace-nowrap">
                        合计金额
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {categoryTotalLoading ? (
                      <tr>
                        <td
                          colSpan={Math.max(2, categoryTotalMatrix.categories.length + 2)}
                          className="py-10 text-center text-foreground-400 text-sm"
                        >
                          <i className="ri-loader-4-line animate-spin mr-1.5" />
                          正在加载分类合计...
                        </td>
                      </tr>
                    ) : categoryTotalError ? (
                      <tr>
                        <td
                          colSpan={Math.max(2, categoryTotalMatrix.categories.length + 2)}
                          className="py-10 text-center text-sm"
                        >
                          <p className="text-foreground-500 mb-2">{categoryTotalError}</p>
                          <button
                            type="button"
                            onClick={() => void fetchCategoryTotalSummary()}
                            className="text-primary-600 hover:text-primary-700 text-xs"
                          >
                            重新加载
                          </button>
                        </td>
                      </tr>
                    ) : categoryTotalMatrix.rows.length === 0 ? (
                      <tr>
                        <td
                          colSpan={Math.max(2, categoryTotalMatrix.categories.length + 2)}
                          className="py-8 text-center text-foreground-400 text-sm"
                        >
                          暂无数据
                        </td>
                      </tr>
                    ) : (
                      categoryTotalMatrix.rows.map((row) => (
                        <tr
                          key={row.supplierId}
                          className="hover:bg-background-50 transition-colors"
                        >
                          <td className="py-3 px-4 font-medium text-foreground-800 whitespace-nowrap sticky left-0 bg-white">
                            {row.supplierName}
                          </td>
                          {row.amounts.map((amount, idx) => (
                            <td
                              key={`${row.supplierId}-${categoryTotalMatrix.categories[idx]?.id ?? idx}`}
                              className={`py-3 px-4 text-right whitespace-nowrap ${
                                amount < 0 ? 'text-orange-600' : 'text-foreground-700'
                              }`}
                            >
                              ¥{amount.toFixed(2)}
                            </td>
                          ))}
                          <td
                            className={`py-3 px-4 text-right font-semibold whitespace-nowrap ${
                              row.total < 0 ? 'text-orange-600' : 'text-primary-600'
                            }`}
                          >
                            ¥{row.total.toFixed(2)}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                  {!categoryTotalLoading &&
                    !categoryTotalError &&
                    categoryTotalMatrix.rows.length > 0 && (
                      <tfoot>
                        <tr className="bg-background-50">
                          <td className="py-3 px-4 text-xs font-semibold text-foreground-700 sticky left-0 bg-background-50">
                            合计
                          </td>
                          {categoryTotalMatrix.columnTotals.map((total, idx) => (
                            <td
                              key={`col-total-${categoryTotalMatrix.categories[idx]?.id ?? idx}`}
                              className={`py-3 px-4 text-right text-xs font-bold whitespace-nowrap ${
                                total < 0 ? 'text-orange-600' : 'text-foreground-900'
                              }`}
                            >
                              ¥{total.toFixed(2)}
                            </td>
                          ))}
                          <td
                            className={`py-3 px-4 text-right text-sm font-bold whitespace-nowrap ${
                              categoryTotalMatrix.grandTotal < 0
                                ? 'text-orange-600'
                                : 'text-primary-600'
                            }`}
                          >
                            ¥{categoryTotalMatrix.grandTotal.toFixed(2)}
                          </td>
                        </tr>
                      </tfoot>
                    )}
                </table>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}